/**
 * useUsers.js
 *
 * React Query hooks for user-related data.
 * Components import these instead of writing axios + useState + useEffect.
 *
 * Before (what components used to do):
 *   const [users, setUsers] = useState([]);
 *   const [loading, setLoading] = useState(false);
 *   useEffect(() => {
 *     setLoading(true);
 *     axios.get(`${api}users`, { headers: { Authorization: 'Bearer ' + authToken } })
 *       .then(res => { setUsers(res.data.user); setLoading(false); })
 *       .catch(() => setLoading(false));
 *   }, []);
 *
 * After (what components do now):
 *   const { data: users = [], isLoading, error } = useUsers();
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from 'services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Query Keys — centralized cache key management
// Changing a key here automatically invalidates the right cache everywhere.
// ─────────────────────────────────────────────────────────────────────────────
export const userKeys = {
  all: ['users'],
  options: ['users', 'options'],
  detail: (id) => ['users', id],
  credits: (id) => ['users', 'credits', id],
  balanceHistory: (id) => ['users', 'balance-history', id],
  supportAgents: ['users', 'support-agents'],
  storage: (id) => ['users', 'storage', id],
};

// ─────────────────────────────────────────────────────────────────────────────
// QUERIES (reads)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch all users.
 * Replaces: axios.get(`${api}users`) + useState + useEffect in Users.js, UserStates.js etc.
 *
 * @param {object} options — optional React Query overrides
 */
export const useUsers = (options = {}) => {
  return useQuery({
    queryKey: userKeys.all,
    queryFn: () => userService.getAll(),
    select: (data) => data?.user ?? [],
    ...options,
  });
};

/**
 * Lightweight user list ({id, company_name, name}) for user-select dropdowns.
 * Cached aggressively so navigating between pages that render the dropdown
 * does not trigger another network request.
 *
 * @param {object} options — optional React Query overrides
 */
export const useUserOptions = (options = {}) => {
  return useQuery({
    queryKey: userKeys.options,
    queryFn: () => userService.getOptions(),
    select: (data) => data?.users ?? [],
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    ...options,
  });
};

/**
 * Fetch user credits/balance.
 * Replaces: axios.get(`${api}chek-user/${id}`)
 *
 * @param {string|number} userId
 */
export const useUserCredits = (userId, options = {}) => {
  return useQuery({
    queryKey: userKeys.credits(userId),
    queryFn: () => userService.getCredits(userId),
    select: (data) => data?.balance ?? '00.00',
    enabled: !!userId, // only fetch when userId is available
    ...options,
  });
};

/**
 * Fetch credit/debit history for a user.
 * Replaces: axios.get(`${api}balance-history/${id}`)
 *
 * @param {string|number} userId
 */
export const useBalanceHistory = (userId, options = {}) => {
  return useQuery({
    queryKey: userKeys.balanceHistory(userId),
    queryFn: () => userService.getBalanceHistory(userId),
    select: (data) => {
      const history = data?.allHistory ?? [];
      return [...history].reverse(); // newest first
    },
    enabled: !!userId,
    ...options,
  });
};

/**
 * Fetch support agents list.
 * Replaces: axios.get(`${api}user/support-agents`)
 */
export const useSupportAgents = (options = {}) => {
  const { page = 1, perPage = 15, enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: [...userKeys.supportAgents, page, perPage],
    queryFn: () => userService.getSupportAgents({ page, per_page: perPage }),
    select: (data) => data?.data ?? [],
    enabled,
    ...queryOptions,
  });
};

/**
 * Fetch storage usage stats for a user.
 * Replaces: axios.get(`${api}user/${userId}/storage`)
 *
 * @param {string|number} userId
 */
export const useUserStorage = (userId, options = {}) => {
  return useQuery({
    queryKey: userKeys.storage(userId),
    queryFn: () => userService.getStorage(userId),
    enabled: !!userId,
    ...options,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// MUTATIONS (writes)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Delete a user and automatically refresh the users list.
 *
 * Usage:
 *   const { mutate: deleteUser, isPending } = useDeleteUser();
 *   deleteUser(userId, {
 *     onSuccess: () => message.success('Deleted'),
 *     onError: (err) => message.error('Failed'),
 *   });
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => userService.delete(id),
    onSuccess: () => {
      // Invalidate and refetch user list
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
};

/**
 * Send OTP for impersonation verification.
 */
export const useSendOtp = () => {
  return useMutation({
    mutationFn: (payload) => userService.sendOtp(payload),
  });
};

/**
 * Verify OTP.
 */
export const useVerifyOtp = () => {
  return useMutation({
    mutationFn: (payload) => userService.verifyOtp(payload),
  });
};
